// Conv-34: a first-run guided coach-mark tour (founder-requested).
//
// WHY IT IS NAV-ANCHORED AND NEVER NAVIGATES. The obvious design — walk the user
// through Portfolio, Markets, Insights, Explore — collides with four real traps in
// this app: (1) every page is React.lazy behind Suspense, so a target does not
// exist at navigation time; (2) App.tsx's ScrollToTop forces main.scrollTop = 0 on
// every pathname change and would undo a scroll-into-view; (3) the Markets
// watchlist and the Explore nominees panel live behind LOCAL useState tabs that no
// outside caller can set, so those steps would silently spotlight nothing; and
// (4) VoiceCommandButton and LanguageSwitcher each render TWICE (desktop sidebar +
// mobile header), so one selector matches two nodes, one of them zero-sized.
//
// So the tour spotlights the NAV ENTRY for each destination plus the live
// Dashboard cards — all of which are mounted by AppShell on every route. It
// teaches where things live without moving the user, and it degrades safely:
// ANY step whose target is missing, clipped or invisible is SKIPPED, which is
// what makes it correct on mobile (the sidebar-only rows do not exist there).
//
// ⚠️ KNOWN AND MEASURED, NOT FORGOTTEN: below 1024px the sidebar is
// `hidden … lg:flex` and the bottom bar renders TABS only, so `nav:/app/explore`
// and `nav:/app/privacy` have NO node on a phone and both steps are silently
// dropped — a phone user never gets the Data & Privacy card, which is the trust
// step. That is why the Conv-48 padlock card anchors on the subscription bar
// instead of on `nav:/app/plans`. Closing the privacy gap properly needs a
// mobile-visible entry point for /app/privacy (the Expo app already has one —
// `quick:privacy` on its dashboard), which is nav work, not copy work, and it is
// logged in HANDOFF rather than smuggled in beside a copy batch.
//
// "Skip if the target is missing" is NOT enough for TIER-GATED features, and the
// first version of this file wrongly claimed it was: a gated CONTROL still
// renders for everyone — the mic button is always in the nav, only the modal
// behind it is walled — so a FREE first-run user was shown voice and then a
// paywall. Steps therefore declare `needs` and are filtered on entitlement.
//
// COMPLIANCE: every string is descriptive product orientation. Nothing here may
// become directive or predictive — no buy/sell/should, no "best", no forecast.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useT } from '../i18n';
import { useEntitlements, type FeatureKey } from '../lib/useEntitlements';

const TOUR_KEY = 'sansara.tour.completed';
export const TOUR_EVENT = 'sansara:start-tour';

/** Fire from anywhere (Settings row, onboarding card) to (re)start the tour. */
export const startTour = () => window.dispatchEvent(new CustomEvent(TOUR_EVENT));

const tourSeen = (): boolean => {
  try { return localStorage.getItem(TOUR_KEY) === '1'; } catch { return false; }
};
const markTourSeen = () => {
  try { localStorage.setItem(TOUR_KEY, '1'); } catch { /* storage unavailable — tour simply reruns next visit */ }
};

interface Step {
  /** data-tour attribute value of the element to spotlight. */
  target: string;
  title: string;
  body: string;
  /**
   * Entitlement required for this step to be worth showing. "Skip the step if
   * its target is missing" is NOT sufficient protection here: a tier-gated
   * CONTROL still renders for everyone (the mic button is always in the nav —
   * only the modal behind it is walled), so without this a FREE first-run user
   * gets spotlighted a feature and then a paywall. That is the Conv-29
   * mixed-page trap wearing a new costume, and it is worst at first run.
   */
  needs?: FeatureKey;
}

// Titles/bodies are English source strings — they ARE the i18n keys, and an
// untranslated key falls back to English, so a new locale degrades gracefully.
const STEPS: Step[] = [
  {
    // 🔴 WAS `nav:/app/explore`, WHICH CONV-49 DISSOLVED — and a step whose target
    // resolves to no node is SKIPPED SILENTLY, so leaving it would simply have
    // deleted a card with nothing anywhere reporting it. (`tour-regression.mjs`
    // asserts the counter never REVERTS; it cannot see a card that never appears.)
    //
    // It is repointed rather than deleted, at the destination that most needs
    // explaining: `Connect` is the row that replaced `Invest` in the bottom bar,
    // and it is where a new account's first real action lives.
    //
    // It also closes the last of the desktop-only cards. `nav:/app/*` anchors only
    // exist where that row renders, the rail is `hidden … lg:flex`, and the mobile
    // bar renders TABS only — so before Conv-48 a phone resolved 7 cards against
    // desktop's 9. Privacy got a phone anchor in Conv-48. 2026-10-20: Connect LEFT
    // the bottom bar (the bar is the phone's five now), so below 1024px this anchor
    // is carried by the Dashboard's Connect quick action instead — same attribute,
    // and on desktop the rail row comes first in the DOM and wins.
    target: 'nav:/app/aggregation',
    title: 'Bring your money in',
    body: 'Link a broker, import a CAS statement, or type a holding in yourself. Whatever Sansara cannot reach yet is listed here too, with what it is waiting on.',
  },
  {
    target: 'nav:/app/portfolio',
    title: 'Your holdings live here',
    body: 'Add what you own one at a time, or bring in a whole demat and mutual-fund statement from a single CAS PDF.',
  },
  {
    target: 'nav:/app/markets',
    title: 'Follow companies and prices',
    body: 'Build a watchlist, filter and sort on reported ratios, and check index levels. Sansara shows the numbers and lets you order them. It does not rate companies or pick any for you.',
  },
  {
    target: 'nav:/app/insights',
    // Conv-48, two corrections in one card.
    //
    // 1. The TITLE now matches the word the nav uses. Conv-44 renamed this
    //    destination 'Insights' → 'Progress' in TABS (AppShell.tsx) and on the
    //    mobile quick actions, and the tour kept calling it 'Goals and analysis'
    //    — so the one surface whose entire job is teaching where things live
    //    pointed at a row and named it something else. Navigation is the weakest
    //    measured dimension in the whole sim series.
    // 2. "Goals are free on every plan" was written when a FREE TIER existed.
    //    Conv-37 deleted it. The sentence stayed literally true and read as a
    //    reference to a tier that is gone. The mobile twin flagged this and
    //    deliberately deferred it, because a Tour body is a RUNTIME label
    //    (`t(step.body)`) so a reword mints a key no catalog carries — i.e. it
    //    had to be reworded in the SAME commit as its ten catalog entries. This
    //    is that commit.
    title: 'Goals and progress',
    // Conv-57 R3 — this page is the clearest free/gated mix in the app, and the
    // re-run panel's onboarding major was a tour card pointing at a surface
    // whose floor went unnamed. Goals and the Summary tab are free; the other
    // tabs are gated and now say so on the strip itself — the card says that
    // once, here, so the mixed page is explained before it is reached.
    body: 'Set a savings goal and track progress toward it. Goals are included in every plan — there is nothing to unlock. Some tabs on this page open on a higher plan, and each one names it.',
  },
  {
    target: 'net-worth',
    // Conv-48: the body now says what the figure EXCLUDES. 27 sentences in the
    // night-16/19/21 panel asked the same question — "why is 'NET WORTH' a gross
    // asset sum when Liabilities is a real tab under Aggregation?" — and the
    // answer is that `routes-aggregation.mjs` liabilities are dark: the route
    // returns `liabilities: []` and there is no writer anywhere, so a user
    // CANNOT record a debt. `3bc0440` deliberately unified both headline
    // surfaces on the words "Net worth", which was right; this is the other half
    // the founder's own india-unique-features.md §4D asked for — keep the name,
    // caption what it leaves out. The old body described how the number MOVES,
    // which nobody was confused about.
    title: 'Your net worth',
    body: 'Everything you own, added up — minus the debts you record in Connect → Liabilities. This is your net figure, not a gross sum.',
  },
  {
    target: 'voice',
    title: 'Ask Sansara out loud',
    body: 'Ask a factual question about your own money or the market — “what is my net worth”, “price of TCS”. It answers with facts. It will not tell you what to buy, sell or hold.',
    needs: 'voice_commands',
  },
  {
    target: 'language',
    title: 'Sansara in your language',
    // NO ENUMERATED LIST. This named seven languages; the app has supported ten
    // since Conv-31 (Gujarati, Punjabi and Assamese were added and this string
    // never moved), so a Gujarati speaker was told their language was missing
    // one click from the switcher that offers it. The Settings <option> list was
    // made data-driven off SUPPORTED_LOCALES for exactly this reason — a tour
    // step cannot interpolate that list without becoming a runtime string no
    // catalog can hold, so it points at the switcher instead of counting.
    body: 'Switch the app to any language Sansara supports, at any time, from the globe icon. Some legal and risk wording is machine-translated and not yet checked by a native speaker; if it ever reads differently from the English, the English is what Sansara means.',
  },
  {
    // Conv-48 — THE NEW CARD, and the reason it exists is measured.
    //
    // 46 of the app's 62 sub-tabs are walls below the entry tier and telemetry
    // puts 10.0% of all taps on a tier-locked control; the sim's own summary was
    // "so many taps returned refusals in a row that it reads as an app that
    // mostly says no." Conv-31 fixed the REPETITION (LockedPage collapses a page
    // of identical veils into one card). Nothing ever explained the padlock
    // itself — and on a touch device nothing could, because `TierBadge` renders
    // a lock ICON whose tier name lives only in `title`/`aria-label`, i.e. in a
    // hover tooltip a phone has no way to open.
    //
    // It anchors on the SUBSCRIPTION BAR's CTA, not on the Plans nav row, and
    // that is deliberate: the rail is `hidden … lg:flex`, so every `nav:/app/*`
    // target outside TABS simply does not exist below 1024px. The bar renders at
    // every width and for every state that is not a plain healthy ACTIVE
    // subscription — which is precisely every first-run account, since Conv-37
    // starts everyone on a trial.
    //
    // The claim in the body is checked, not assumed: opening a walled
    // destination renders LockedPage, which prints "It opens from {tier}" with
    // the tier taken from the server's featureTiers via `cheapestTierName`. No
    // tier is hand-typed here — that is the rule this card describes, so naming
    // one in it would be the joke writing itself.
    target: 'plans',
    title: 'When something is locked',
    body: 'Some parts of Sansara open on a higher plan. Open any of them and Sansara names the plan it needs — and every plan and price is listed here.',
  },
  {
    target: 'nav:/app/privacy',
    title: 'You can see who has access',
    body: 'Everything with access to your data is listed here, and you can revoke any of it. You can also export or erase your data outright.',
  },
];

// A non-zero rect is NOT the same as visible: a nav row scrolled out of the
// sidebar's own overflow-y-auto still reports full width and height, so a naive
// size test would ring a rectangle the user cannot see.
const clippedOut = (n: HTMLElement, r: DOMRect): boolean => {
  if (r.bottom <= 0 || r.top >= window.innerHeight || r.right <= 0 || r.left >= window.innerWidth) return true;
  let p = n.parentElement;
  while (p && p !== document.body) {
    const st = getComputedStyle(p);
    if (/(auto|scroll|hidden)/.test(`${st.overflowY} ${st.overflowX}`)) {
      const pr = p.getBoundingClientRect();
      if (r.bottom <= pr.top + 1 || r.top >= pr.bottom - 1 || r.right <= pr.left + 1 || r.left >= pr.right - 1) return true;
    }
    p = p.parentElement;
  }
  return false;
};

// Pick the on-screen node when there is one, but fall back to any RENDERED node.
// The distinction matters: a duplicate that is display:none (the desktop sidebar
// copy at mobile width) has a zero rect and must never be chosen, whereas a real
// target merely scrolled out of the sidebar's own overflow IS the right element —
// dropping it would silently cost the step. Those are scrolled into view before
// measuring instead. Getting this wrong in either direction is a defect: too
// strict skipped Data & Privacy (the trust step) on a 900px screen; too lax rang
// an invisible rectangle.
const findTarget = (name: string): HTMLElement | null => {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(name)}"]`))
    .filter((n) => { const r = n.getBoundingClientRect(); return r.width > 0 && r.height > 0; });
  return nodes.find((n) => !clippedOut(n, n.getBoundingClientRect())) ?? nodes[0] ?? null;
};

export function Tour() {
  const t = useT();
  const { can } = useEntitlements();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Only the steps whose targets exist right now — resolved when the tour opens
  // so the step counter ("2 of 6") never counts a step that will be skipped.
  const [steps, setSteps] = useState<Step[]>([]);

  const finish = useCallback(() => {
    setOpen(false);
    setIdx(0);
    markTourSeen();
  }, []);

  // 🔴 `begin` MUST KEEP A STABLE IDENTITY, AND THIS IS NOT TIDINESS.
  //
  // `useEntitlements()` builds a fresh object with a fresh `can` closure on EVERY
  // render (src/lib/useEntitlements.ts — now memoised, but this must not depend on
  // that staying true). A `useCallback([can])` therefore changed identity on every
  // render, which re-fired the first-run autostart effect below, which ~700ms later
  // called `begin()` again — and `begin` does `setIdx(0)`.
  //
  // The symptom, reported from a real phone: press Next on card 1, land on card 2,
  // and a couple of seconds later get thrown back to card 1, for ever, so the only
  // way out is Skip. Pressing Next is itself a re-render, so the loop fed itself.
  // It was never mobile-specific — it hit every browser; it was just most obvious
  // where the cards are full-screen. The mobile Expo twin already carried this exact
  // fix and its comment names the cause; the web original never got it.
  //
  // So: read `can` through a ref written in an EFFECT (never during render), and
  // give `begin` an empty dep list so it is allocated once per mount.
  const canRef = useRef(can);
  useEffect(() => { canRef.current = can; }, [can]);

  /**
   * @param auto true only for the first-run autostart.
   *
   * The `auto` split matters on the EMPTY case. The seen flag means "the first-run
   * tour has had its chance", so an autostart that resolves no steps must write it
   * or every future visit re-polls. A MANUAL replay that resolves nothing must NOT
   * write it — the usual reason is that the caller is on a page holding no targets,
   * and burning the flag there silently disarms the tour for someone who just asked
   * to see it. (Mirrors the mobile twin, which fixed this first.)
   */
  const begin = useCallback((auto: boolean) => {
    // Two independent filters: the user must be able to USE the feature, and the
    // element must actually be on screen to point at.
    const live = STEPS.filter((s) => (!s.needs || canRef.current(s.needs)) && findTarget(s.target));
    if (!live.length) { if (auto) markTourSeen(); return; }  // never open an empty tour
    setSteps(live);
    setIdx(0);
    setOpen(true);
  }, []);

  // Manual re-entry (Settings / onboarding card).
  useEffect(() => {
    const h = () => begin(false);
    window.addEventListener(TOUR_EVENT, h);
    return () => window.removeEventListener(TOUR_EVENT, h);
  }, [begin]);

  // First run. A fixed delay is the wrong tool: the step set is resolved ONCE, and
  // the first step's target (the net-worth stat cards) only mounts after
  // /api/portfolio/summary resolves — behind a React.lazy chunk. On anything
  // slower than localhost the timer fired first and the user permanently lost
  // step 1, because the seen flag is then written for good. So POLL for the first
  // target and open as soon as it is there, with a hard ceiling so a user who
  // genuinely has no dashboard still gets the rest of the tour.
  // Belt AND braces: `begin` is stable now, but the autostart is also allowed to
  // fire only ONCE per mount. A first-run tour that can re-enter itself is the bug
  // above, and the cheapest guarantee that it never comes back through some future
  // dependency is to make re-entry structurally impossible rather than merely
  // unlikely. Manual replay (the TOUR_EVENT listener) is deliberately unaffected.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (tourSeen() || autoStartedRef.current) return undefined;
    let stop = false;
    const deadline = 6000;
    const started = performance.now();
    const tick = () => {
      if (stop) return;
      if (findTarget(STEPS[0].target) || performance.now() - started > deadline) {
        autoStartedRef.current = true;
        begin(true);
        return;
      }
      window.setTimeout(tick, 200);
    };
    const id = window.setTimeout(tick, 700);
    return () => { stop = true; window.clearTimeout(id); };
  }, [begin]);

  // Track the spotlit rect across resize/scroll.
  useLayoutEffect(() => {
    if (!open || !steps[idx]) return undefined;
    const measure = () => {
      const el = findTarget(steps[idx].target);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    // Bring a clipped target into view FIRST, then measure. Safe here because the
    // tour never navigates, so App.tsx's ScrollToTop (which resets main.scrollTop
    // on every pathname change) is not racing us.
    const target = findTarget(steps[idx].target);
    if (target && clippedOut(target, target.getBoundingClientRect())) {
      try { target.scrollIntoView({ block: 'center', inline: 'nearest' }); } catch { /* older browsers */ }
    }
    measure();
    const main = document.querySelector('main');
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    main?.addEventListener('scroll', measure);
    // Scroll/resize alone miss the common case: the target RESIZES under the
    // spotlight when late data lands (the net-worth skeleton becoming three
    // populated cards), which would leave the ring measuring the skeleton.
    const el = findTarget(steps[idx].target);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) { if (el) ro.observe(el); ro.observe(document.body); }
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      main?.removeEventListener('scroll', measure);
      ro?.disconnect();
    };
  }, [open, idx, steps]);

  // Escape closes; focus the primary button each step; and CONTAIN Tab inside the
  // card. aria-modal is only a hint — without a trap, Tab walks straight through
  // the scrim onto the nav behind it and the user can navigate away mid-tour
  // (the click-catcher stops pointers, not keyboards).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { finish(); return; }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const f = Array.from(cardRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'));
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      const active = document.activeElement;
      const inside = cardRef.current.contains(active);
      if (e.shiftKey && (active === first || !inside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (active === last || !inside)) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    nextRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, idx, finish]);

  if (!open || !steps.length) return null;
  const step = steps[idx];
  const last = idx === steps.length - 1;

  // Tooltip placement: below the target when there is room, otherwise above;
  // always clamped inside the viewport.
  const pad = 10;
  const width = Math.min(320, window.innerWidth - 24);
  const below = rect ? rect.bottom + pad : window.innerHeight / 2;
  const roomBelow = rect ? window.innerHeight - rect.bottom > 220 : true;
  const CARD_H = 200;   // conservative; the card is ~150-200px tall at these widths
  const rawTop = rect ? (roomBelow ? below : rect.top - 210) : window.innerHeight / 2 - 100;
  // `left` was clamped both ways from the start; `top` was not, so a target
  // scrolled off-screen pushed the card out of view entirely.
  const top = Math.min(Math.max(12, rawTop), Math.max(12, window.innerHeight - CARD_H - 12));
  const left = rect
    ? Math.min(Math.max(12, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 12)
    : window.innerWidth / 2 - width / 2;

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={t('Product tour')}>
      {/* Scrim + spotlight in one: a transparent window at the target, with an
          enormous ring shadow dimming everything outside it. */}
      {rect ? (
        <div
          className="pointer-events-none absolute rounded-xl ring-2 ring-brand-400/70 transition-all duration-200"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            boxShadow: '0 0 0 9999px rgba(23, 21, 18, 0.62)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-ink/60" />
      )}

      {/* Click-catcher so the page underneath cannot be operated mid-tour. */}
      <button
        type="button"
        aria-label={t('Skip tour')}
        onClick={finish}
        className="absolute inset-0 h-full w-full cursor-default"
        tabIndex={-1}
      />

      <div
        ref={cardRef}
        className="absolute rounded-2xl bg-surface p-4 shadow-pop dark:bg-surface-dark-raised"
        style={{ top, left, width }}
      >
        <button
          type="button"
          onClick={finish}
          aria-label={t('Skip tour')}
          className="absolute right-2 top-2 rounded-full p-1 text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink-soft dark:text-slate-400 dark:hover:bg-slate-800"
        >
          <X size={15} />
        </button>
        <p className="pr-6 font-display text-sm font-bold">{t(step.title)}</p>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-soft dark:text-slate-300">{t(step.body)}</p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-xs tabular-nums text-ink-faint dark:text-slate-400">
            {t('{current} of {total}', { current: idx + 1, total: steps.length })}
          </span>
          <div className="flex items-center gap-2">
            {idx > 0 ? (
              <button type="button" onClick={() => setIdx((i) => i - 1)} className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-ink-soft hover:bg-surface-sunken dark:text-slate-300 dark:hover:bg-slate-800">
                {t('Back')}
              </button>
            ) : (
              <button type="button" onClick={finish} className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-ink-soft hover:bg-surface-sunken dark:text-slate-300 dark:hover:bg-slate-800">
                {t('Skip')}
              </button>
            )}
            <button
              ref={nextRef}
              type="button"
              onClick={() => (last ? finish() : setIdx((i) => i + 1))}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
            >
              {last ? t('Done') : t('Next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
